import type { ArchitectureDiagram, ArchitectureEdge, ArchitectureNode, EdgeDirection, NodeCategory } from './types';

export interface JsonParseError {
  message: string;
}

const VALID_CATEGORIES: NodeCategory[] = [
  'database',
  'orm',
  'api',
  'framework',
  'external',
  'infra',
  'client',
  'generic',
];

const VALID_DIRECTIONS: EdgeDirection[] = ['forward', 'backward', 'bidirectional', 'none'];

/** Serializes a diagram to pretty-printed JSON — the "source" view for architecture
 * diagrams, mirroring how the DB diagram round-trips through DBML text. */
export function generateArchitectureJson(diagram: ArchitectureDiagram): string {
  return JSON.stringify(diagram, null, 2);
}

/**
 * Parses hand-edited JSON back into a diagram. Unlike DBML's forgiving recursive-descent
 * parser, invalid JSON can't be partially recovered — on any structural or reference error
 * this returns `diagram: null` so the caller keeps the last known-good diagram and just
 * surfaces the error messages.
 */
export function parseArchitectureJson(text: string): { diagram: ArchitectureDiagram | null; errors: JsonParseError[] } {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch (err) {
    return { diagram: null, errors: [{ message: err instanceof Error ? err.message : 'Invalid JSON' }] };
  }

  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return { diagram: null, errors: [{ message: 'Root value must be an object with "nodes" and "edges" arrays.' }] };
  }
  const obj = raw as Record<string, unknown>;
  const rawNodes = obj.nodes;
  const rawEdges = obj.edges;

  const errors: JsonParseError[] = [];
  if (!Array.isArray(rawNodes)) errors.push({ message: '"nodes" must be an array.' });
  if (!Array.isArray(rawEdges)) errors.push({ message: '"edges" must be an array.' });
  if (errors.length) return { diagram: null, errors };

  // Pass 1: validate each node's own fields and collect the id set, so pass 2 can check
  // forward references (an edge, or a node's parentId, pointing at any other node
  // regardless of array order).
  const nodeIds = new Set<string>();
  (rawNodes as unknown[]).forEach((entry, i) => {
    if (typeof entry !== 'object' || entry === null) {
      errors.push({ message: `nodes[${i}] must be an object.` });
      return;
    }
    const n = entry as Record<string, unknown>;
    if (typeof n.id !== 'string' || !n.id) {
      errors.push({ message: `nodes[${i}] is missing a string "id".` });
      return;
    }
    if (nodeIds.has(n.id)) {
      errors.push({ message: `Duplicate node id "${n.id}".` });
      return;
    }
    nodeIds.add(n.id);
  });

  const nodes: ArchitectureNode[] = [];
  (rawNodes as unknown[]).forEach((entry, i) => {
    if (typeof entry !== 'object' || entry === null) return; // already reported above
    const n = entry as Record<string, unknown>;
    if (typeof n.id !== 'string' || !n.id) return; // already reported above
    if (typeof n.kind !== 'string' || !n.kind) {
      errors.push({ message: `Node "${n.id}" is missing a string "kind".` });
      return;
    }
    if (typeof n.category !== 'string' || !VALID_CATEGORIES.includes(n.category as NodeCategory)) {
      errors.push({ message: `Node "${n.id}" has an invalid "category" (expected one of ${VALID_CATEGORIES.join(', ')}).` });
      return;
    }
    if (typeof n.label !== 'string' || !n.label) {
      errors.push({ message: `Node "${n.id}" is missing a string "label".` });
      return;
    }
    if (typeof n.parentId === 'string' && !nodeIds.has(n.parentId)) {
      errors.push({ message: `Node "${n.id}" has a "parentId" that doesn't match any node.` });
      return;
    }

    const node: ArchitectureNode = { id: n.id, kind: n.kind, category: n.category as NodeCategory, label: n.label };
    if (typeof n.note === 'string') node.note = n.note;
    if (typeof n.color === 'string') node.color = n.color;
    if (n.mode === 'component' || n.mode === 'group') node.mode = n.mode;
    if (typeof n.parentId === 'string' || n.parentId === null) node.parentId = n.parentId as string | null;
    if (n.position && typeof n.position === 'object') {
      const p = n.position as Record<string, unknown>;
      if (typeof p.x === 'number' && typeof p.y === 'number') node.position = { x: p.x, y: p.y };
    }
    nodes.push(node);
  });

  const edges: ArchitectureEdge[] = [];
  const edgeIds = new Set<string>();
  (rawEdges as unknown[]).forEach((entry, i) => {
    if (typeof entry !== 'object' || entry === null) {
      errors.push({ message: `edges[${i}] must be an object.` });
      return;
    }
    const e = entry as Record<string, unknown>;
    if (typeof e.id !== 'string' || !e.id) {
      errors.push({ message: `edges[${i}] is missing a string "id".` });
      return;
    }
    if (edgeIds.has(e.id)) {
      errors.push({ message: `Duplicate edge id "${e.id}".` });
      return;
    }
    if (typeof e.fromNodeId !== 'string' || !nodeIds.has(e.fromNodeId)) {
      errors.push({ message: `Edge "${e.id}" has a "fromNodeId" that doesn't match any node.` });
      return;
    }
    if (typeof e.toNodeId !== 'string' || !nodeIds.has(e.toNodeId)) {
      errors.push({ message: `Edge "${e.id}" has a "toNodeId" that doesn't match any node.` });
      return;
    }
    edgeIds.add(e.id);

    const direction: EdgeDirection = VALID_DIRECTIONS.includes(e.direction as EdgeDirection)
      ? (e.direction as EdgeDirection)
      : 'forward';
    const edge: ArchitectureEdge = {
      id: e.id,
      fromNodeId: e.fromNodeId,
      toNodeId: e.toNodeId,
      direction,
      animated: typeof e.animated === 'boolean' ? e.animated : true,
    };
    if (typeof e.label === 'string') edge.label = e.label;
    if (typeof e.speed === 'number') edge.speed = e.speed;
    if (typeof e.fromHandle === 'string' || e.fromHandle === null) edge.fromHandle = e.fromHandle as string | null;
    if (typeof e.toHandle === 'string' || e.toHandle === null) edge.toHandle = e.toHandle as string | null;
    edges.push(edge);
  });

  if (errors.length) return { diagram: null, errors };
  return { diagram: { nodes, edges }, errors: [] };
}
